import { index, modelOptions, mongoose, post, prop, PropType, Ref } from "@typegoose/typegoose";
import { z } from "zod";
import type { Organization } from "../organization/organization.js";
import { getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { MessagingChannelAddress } from "../commonSubSchemas/messagingChannelAddress.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { MessagingContactFieldsWithDefaults, MessagingContactSchemaTypes } from "../types/messagingContact.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import { QuietHoursWindow } from "./subSchemas/quietHoursWindow.js";

export { QuietHoursWindow } from "./subSchemas/quietHoursWindow.js";

const SCHEMA_KEY = "messagingContacts" as const;

/**
 * Someone we can message and their delivery policy. Global and deduplicated by `channelAddress` — a number
 * can belong to many organizations, so consent and preferences live on the number, not on any subscription.
 * SCD Type-2: consent and preference changes keep a versioned history; the stable identity across
 * versions is `recordId`, so other collections (memberships) reference the number by recordId.
 */
@index(
    { "channelAddress.channel": 1, "channelAddress.address": 1 },
    { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } },
)
@index({ memberOrganizations: 1 }, { partialFilterExpression: { versionIsCurrent: true, deleted: false } })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "messagingContacts" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<MessagingContact>>(...addSchemaKeyHook(SCHEMA_KEY))
export class MessagingContact extends ScdDocument<MessagingContact, MessagingContactFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /**
     * The address this contact is reached at; unique among current, non-deleted versions (see the
     * partial index above). Response-only: it is set at enrollment, never edited in place.
     */
    @validate({ childClass: MessagingChannelAddress, responseOnly: true })
    @prop({ type: () => MessagingChannelAddress, required: true })
    public channelAddress!: MessagingChannelAddress;

    /** Optional friendly name for the number, e.g. "John Smith". Global to the number. */
    @validate({ zodValidator: z.string().min(1).optional() })
    @prop({ type: () => String, required: false })
    public name?: string;

    /** Whether the number has been verified. Response-only: set by the verification flow, not clients. */
    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public verified: boolean = false;

    /** Preferred locale for rendering messages, e.g. "en-US". */
    @validate({ zodValidator: z.string().min(1), hasDefault: true })
    @prop({ type: () => String, required: true, default: "en-US" })
    public locale: string = "en-US";

    /** When set (via a STOP reply), all sends to this number are suppressed. Response-only: set by the STOP flow. */
    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public optedOut: boolean = false;

    /** Optional quiet-hours window; while open, standard alerts defer until it ends. Nullable so an update can clear it. */
    @validate({ childClass: QuietHoursWindow, childClassTransform: (object) => object.nullable().optional() })
    @prop({ type: () => QuietHoursWindow, required: false, _id: false })
    public quietHours?: QuietHoursWindow;

    /** Whether critical alerts may pierce quiet hours for this number. */
    @validate({ zodValidator: z.boolean(), hasDefault: true })
    @prop({ type: () => Boolean, required: true, default: true })
    public allowCriticalWarningsToInterruptQuietHours: boolean = true;

    // Internal org↔address association, never part of any API contract (a globally deduplicated number
    // must not disclose the other orgs tied to it). Intentionally NOT @validate-decorated so it stays
    // out of every generated DTO variant; it is read/written directly on the model by the enroll flows.
    /** Organizations this number is enrolled in; the direct org↔address association. */
    @prop({ type: () => [mongoose.Types.ObjectId], required: true, default: [], ref: "Organization" }, PropType.ARRAY)
    public memberOrganizations: Ref<Organization>[] = [];

    /** Organizations this number has opted out of; enrollment into these is refused. */
    @prop({ type: () => [mongoose.Types.ObjectId], required: true, default: [], ref: "Organization" }, PropType.ARRAY)
    public optedOutOrganizations: Ref<Organization>[] = [];

    static getters = {} as const satisfies Record<string, (self: MessagingContact) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<MessagingContactSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<MessagingContact, MessagingContactFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

MessagingContact.dtoValidators = getZodSchemaFromClass(
    MessagingContact,
) as unknown as DtoValidatorTypes<MessagingContactSchemaTypes>;
